import { useTranslation } from "react-i18next";
import styled from "styled-components";

import BatteryIcon from "../../../assets/driverAppV2/battery.svg?react";
import fuelCar from "../../../assets/driverAppV2/fuel-car.png";
import electricCar from "../../../assets/driverAppV2/electric-car.png";
import { colors } from "../../../styles/figmaColors";
import { typographies } from "../../../styles/figmaTypographies";
import { StyledColumn, StyledFlex } from "./layout";
import { Spacer } from "../Spacer";
import { TextCapitalized } from "../text/TextCapitalized";

export interface VehicleOverviewProps {
  energy: "electric" | "fuel";
  battery: number | null;
  rangeKm: number | null;
  mileage: number | null;
  brand: string;
  model: string;
  imageSrc: string | null;
}

const mileageFormat = new Intl.NumberFormat("fr-FR");

export const VehicleOverview = ({
  energy,
  battery,
  rangeKm,
  mileage,
  brand,
  model,
  imageSrc,
}: VehicleOverviewProps) => {
  const { t } = useTranslation();
  const defaultIcon = energy === "electric" ? electricCar : fuelCar;

  return (
    <StyledColumn>
      {energy === "electric" ? (
        <>
          <StyledBatteryRow>
            <StyledIcon>
              <BatteryIcon />
            </StyledIcon>
            <Spacer x={0.375} />
            <StyledPercentRow>
              {battery !== null && <StyledPercent>{battery}</StyledPercent>}
              <StyledPercentSign>
                {battery === null ? "- %" : "%"}
              </StyledPercentSign>
            </StyledPercentRow>
          </StyledBatteryRow>
          <Spacer y={0.25} />
          <StyledRange>
            {t("vehicleOverview.range", { km: rangeKm ?? "-" })}
          </StyledRange>
          <Spacer y={0.125} />
          <TextCapitalized>
            <StyledRangeLabel>
              {t("vehicleOverview.estimatedRange")}
            </StyledRangeLabel>
          </TextCapitalized>
        </>
      ) : (
        <>
          <StyledRange>
            {t("vehicleOverview.range", {
              km: mileage === null ? "-" : mileageFormat.format(mileage),
            })}
          </StyledRange>
          <Spacer y={0.125} />
          <TextCapitalized>
            <StyledRangeLabel>{t("vehicleOverview.mileage")}</StyledRangeLabel>
          </TextCapitalized>
        </>
      )}
      <Spacer y={0.5} />
      <StyledCar src={imageSrc ?? defaultIcon} alt={`${brand} ${model}`} />
    </StyledColumn>
  );
};

const StyledBatteryRow = styled(StyledFlex)`
  align-items: baseline;
`;

const StyledIcon = styled(StyledFlex)`
  svg {
    height: 1.8rem;
    width: auto;
  }
`;

const StyledPercentRow = styled(StyledFlex)`
  align-items: baseline;
`;

const StyledPercent = styled.span`
  ${typographies["Header/H1"]}
  font-size: 3rem;
  line-height: 1;
  color: ${colors["colors/text/black"]};
`;

const StyledPercentSign = styled.span`
  ${typographies["Header/H2"]}
  color: ${colors["colors/text/black"]};
`;

const StyledRange = styled.span`
  ${typographies["Header/H2"]}
  color: ${colors["colors/text/black"]};
`;

const StyledRangeLabel = styled.span`
  ${typographies["Body/XXS"]}
  color: ${colors["colors/text/darkGrey"]};
`;

const StyledCar = styled.img`
  display: block;
  width: 100%;
`;
